import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { ShieldAlert } from 'lucide-react';
import Login from 'pages/login/Login';
import Dashboard from 'pages/dashboard/Dashboard';
import Admin from 'pages/admin/AdminDashboard';
import SubscriptionManagement from 'pages/admin/SubscriptionManagement';
import AccountsAnalysis from 'pages/admin/AccountsAnalysis';
import { UserManagement, AccountsManagement, JobManagement, QueueManagement, AnboxManagement, ProxiesManagement } from 'pages/superAdmin';
import Logout from 'pages/logout/Logout';
import Registration from 'pages/registration/Registration';
import VerifyOTP from 'components/auth/VerifyOTP';
import Welcome from 'pages/welcome/Welcome';
import Rules from 'pages/rules/Rules';
import NewRule from 'pages/rules/NewRule';
import RuleDetail from 'pages/rules/RuleDetail';
import ForgotPassword from 'components/auth/ForgotPassword';
import ResetPassword from 'components/auth/ResetPassword';
import Subscriptions from 'pages/subscriptions/Subscriptions';

import PaymentSuccess from 'pages/subscriptions/PaymentSuccess';
import PaymentCancel from 'pages/subscriptions/PaymentCancel';
import Billing from 'pages/billing/Billing';
import Accounts from 'pages/accounts/Accounts';
import Posts from 'pages/actions/Posts';
import Story from 'pages/actions/Story';
import Messages from 'pages/actions/Messages';
import Comments from 'pages/actions/Comments';
import Likes from 'pages/actions/Likes';
import Followings from 'pages/actions/Followings';
import Settings from 'pages/settings/Settings';
import Famousclicker from 'pages/landing/FamousClicker';
import AudienceSettingsTab from 'components/accounts/AudienceSettingsTab';
import AccountSettingsTab from 'components/accounts/AccountSettingsTab';
import Support from 'pages/support/Support';
import AuthLayout from 'components/layouts/AuthLayout';
import DashboardLayout from 'components/layouts/DashboardLayout';
import AccountsLayout from 'components/layouts/AccountsLayout';
import Tabs from 'components/accounts/Tabs';
import ProtectedRoute from 'components/auth/ProtectedRoute';
import { RootState } from 'config/redux/store';

// Define component props
interface RouterProps {
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const CategoryGuard: React.FC<{ category: string; children: React.ReactNode }> = ({ category, children }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const subscriptionCategories = useSelector((state: RootState) => state.user.subscriptionCategories);

  if (subscriptionCategories.length > 0 && !subscriptionCategories.includes(category)) {
    return (
      <div className="flex flex-col items-center justify-center h-full" style={{ gap: '16px', padding: '48px' }}>
        <div className="flex items-center justify-center" style={{ width: '56px', height: '56px', borderRadius: '16px', background: isDark ? '#1D293D' : '#F1F5F9' }}>
          <ShieldAlert size={28} color={isDark ? '#62748E' : '#90A1B9'} />
        </div>
        <p style={{ fontSize: '16px', fontWeight: 600, color: isDark ? '#E2E8F0' : '#314158', textAlign: 'center' }}>
          {t('accessRestricted.title', 'Access Restricted')}
        </p>
        <p style={{ fontSize: '14px', fontWeight: 400, color: isDark ? '#90A1B9' : '#62748E', textAlign: 'center', maxWidth: '400px' }}>
          {t('accessRestricted.message', "You don't have access to this feature. Please upgrade your subscription.")}
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: '8px', padding: '8px 20px', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }}
        >
          {t('accessRestricted.backToDashboard', 'Back to Dashboard')}
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

const Router: React.FC<RouterProps> = ({ setIsLoggedIn }) => {
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/" element={<Famousclicker />} />
        <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/registration" element={<Registration setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/verify-otp" element={<VerifyOTP />} />
        <Route path="/welcome" element={<Welcome setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Route>

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
  <Route element={<DashboardLayout setIsLoggedIn={setIsLoggedIn} />}>
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/rules" element={<Rules />} />
    <Route path="/rules/new" element={<NewRule />} />
    <Route path="/rules/:ruleId" element={<RuleDetail />} />
    <Route path="/subscriptions" element={<Subscriptions />} />

    <Route path="/billing" element={<Billing />} />
    <Route path="/payment/success" element={<PaymentSuccess />} />
    <Route path="/payment/cancel" element={<PaymentCancel />} />
    <Route path="/actions/messages" element={<CategoryGuard category="Messages"><Messages selectedUser={selectedUser} /></CategoryGuard>} />
    <Route path="/actions/comments" element={<CategoryGuard category="Comments"><Comments selectedUser={selectedUser} /></CategoryGuard>} />
    <Route path="/actions/likes" element={<CategoryGuard category="Likes"><Likes selectedUser={selectedUser} /></CategoryGuard>} />
    <Route path="/actions/followings" element={<CategoryGuard category="Following"><Followings selectedUser={selectedUser} /></CategoryGuard>} />
    <Route path="/actions/posts" element={<CategoryGuard category="Posts"><Posts selectedUser={selectedUser} /></CategoryGuard>} />
    <Route path="/actions/story" element={<CategoryGuard category="Story"><Story selectedUser={selectedUser} /></CategoryGuard>} />
    <Route path="/settings" element={<Settings />} />
    <Route path="/help" element={<Navigate to="/support" replace />} />
    <Route path="/support" element={<Support />} />
    <Route path="/accounts" element={<Accounts />}>
      <Route path=":id" element={<AccountsLayout />}>
        <Route path="tabs" element={<Tabs />} />
        <Route path="audience-settings" element={<AudienceSettingsTab />} />
        <Route path="account-settings" element={<AccountSettingsTab />} />
      </Route>
    </Route>
  </Route>
</Route>


      {/* Admin Routes with Role Restriction */}
      <Route element={<ProtectedRoute allowedRoles={['Admin', 'SuperAdmin']} />}>
        <Route element={<DashboardLayout setIsLoggedIn={setIsLoggedIn} />}>
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/subscriptions" element={<SubscriptionManagement />} />
          <Route path="/admin/accounts-analysis" element={<AccountsAnalysis />} />
        </Route>
      </Route>

      {/* Super Admin Routes - Only SuperAdmin role */}
      <Route element={<ProtectedRoute allowedRoles={['SuperAdmin']} />}>
        <Route element={<DashboardLayout setIsLoggedIn={setIsLoggedIn} />}>
          <Route path="/super-admin/users" element={<UserManagement />} />
          <Route path="/super-admin/accounts" element={<AccountsManagement />} />
          <Route path="/super-admin/jobs" element={<JobManagement />} />
          <Route path="/super-admin/queue" element={<QueueManagement />} />
          <Route path="/super-admin/anbox" element={<AnboxManagement />} />
          <Route path="/super-admin/proxies" element={<ProxiesManagement />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default Router;